/* @refresh reset */

import { useLayoutEffect, useRef, useState } from "react"

export const SHOW_NOTHING_MS = 500 // don't show skeleton unless work is still going after 500ms
export const MIN_SHOW_SKELETON_MS = 1000 // show skeleton for at least 1 second

export type SkeletonPhase = "showNothing" | "showSkeleton" | "showResult"

/** This hook stamps the clock once on this instance's first render. It does not use the route or App, because `location.key` restamps mid-navigation and then the showNothing timeout never fires. */
function useMountedAt(): number {
  const originRef = useRef<number | null>(null)
  if (originRef.current == null) {
    // eslint-disable-next-line react-hooks/purity -- this instance reads the clock once on first render.
    originRef.current = performance.now()
  }
  return originRef.current
}

/**
 * This hook times the three phases a content area paints: nothing, a `<Skeleton />`, or the result. Callers switch on the return value and render `<Skeleton />` for `showSkeleton`.
 * While `loading` is true (work is still going), the hook stays in `showNothing` for `SHOW_NOTHING_MS` of mount wall time. If work is still going after that deadline, it returns `showSkeleton`. Once it has returned `showSkeleton`, it holds that phase for `MIN_SHOW_SKELETON_MS`.
 * Frozen time counts: if `showSkeleton` has already lasted `MIN_SHOW_SKELETON_MS`, the hook does not hold extra after work finishes.
 * Fast loads that finish before `SHOW_NOTHING_MS` never return `showSkeleton`.
 * `showNothing` is chrome only and must not show empty-state copy. `showSkeleton` is the `<Skeleton />`, including the hold. `showResult` is the data or the empty copy.
 *
 * Both timers run once per mount. `skeletonShown` does not reset when `loading` goes true again on the same instance.
 * Same-route id switches remount the page. Roster keys `RosterPage` on `managerUid` / `creatorUid` so that remount happens.
 */
export function useSkeletonPhases(loading: boolean): SkeletonPhase {
  const mountedAt = useMountedAt()
  const loadingRef = useRef(loading)
  loadingRef.current = loading
  const skeletonShownAtRef = useRef(0)
  const mountedAtRef = useRef(mountedAt)
  const [skeletonShown, setSkeletonShown] = useState(false)
  const [holdMet, setHoldMet] = useState(false)

  // If this instance somehow receives a new mount stamp, reset both timers. That should not happen, because the clock is taken on first render only.
  if (mountedAtRef.current !== mountedAt) {
    mountedAtRef.current = mountedAt
    if (skeletonShown) {
      setSkeletonShown(false)
    }
    if (holdMet) {
      setHoldMet(false)
    }
  }

  // This effect arms the showNothing deadline once. If work is still going when the deadline fires, the hook returns `showSkeleton`.
  useLayoutEffect(() => {
    const showSkeletonIfLoading = () => {
      if (!loadingRef.current) {
        return
      }
      skeletonShownAtRef.current = performance.now()
      setSkeletonShown(true)
    }
    const remainingShowNothing = SHOW_NOTHING_MS - (performance.now() - mountedAt)
    if (remainingShowNothing <= 0) {
      showSkeletonIfLoading()
      return
    }
    const timeout = window.setTimeout(showSkeletonIfLoading, remainingShowNothing)
    return () => {
      window.clearTimeout(timeout)
    }
  }, [mountedAt])

  // If loading becomes true after the deadline has already passed — the content area mounted late, or it was still empty when the timeout ran with loading false — this effect returns `showSkeleton` now.
  useLayoutEffect(() => {
    if (skeletonShown || !loading) {
      return
    }
    if (performance.now() - mountedAt >= SHOW_NOTHING_MS) {
      skeletonShownAtRef.current = performance.now()
      setSkeletonShown(true)
    }
  }, [loading, mountedAt, skeletonShown])

  // Once the hook has entered `showSkeleton`, this effect holds that phase for MIN_SHOW_SKELETON_MS of wall time, including time spent there while work is still going.
  useLayoutEffect(() => {
    if (!skeletonShown || holdMet) {
      return
    }
    const remainingMinShowSkeleton = MIN_SHOW_SKELETON_MS - (performance.now() - skeletonShownAtRef.current)
    const meet = () => {
      setHoldMet(true)
    }
    if (remainingMinShowSkeleton <= 0) {
      meet()
      return
    }
    const timeout = window.setTimeout(meet, remainingMinShowSkeleton)
    return () => {
      window.clearTimeout(timeout)
    }
  }, [holdMet, mountedAt, skeletonShown])

  if (skeletonShown && (loading || !holdMet)) {
    return "showSkeleton"
  }
  if (!loading) {
    return "showResult"
  }
  return "showNothing"
}
